import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConstantsService } from '@catch/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, take } from 'rxjs';
import { JobboardAdapter } from '../adapters/jobboard.adapter';
import { Job } from '../interfaces/job';
import { JobboardFilter } from '../interfaces/jobboard-filter';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class JobsService {
  private readonly jobboardAdapter = inject(JobboardAdapter);
  private readonly constantsService = inject(ConstantsService);
  private readonly router = inject(Router);

  public readonly LIMIT: number = 25;

  jobs: Job[];
  currentFilter: {
    searchQuery: string;
    facilities: string[];
    employmentTypes: number[];
    workingTimes: number[];
    companyIds: number[];
    pageIndex: number;
  };
  filterOptions: JobboardFilter;
  jobsLoading: boolean;
  jobsTotal: number;
  initialFilters: boolean;
  showFilter: {
    general: boolean;
    subFilters: {
      searchQuery: boolean;
      facilities: boolean;
      employmentTypes: boolean;
      workingTimes: boolean;
    };
  };

  constructor() {
    this.jobsLoading = false;
    this.jobs = [];
    this.currentFilter = {
      searchQuery: '',
      facilities: [],
      employmentTypes: [],
      workingTimes: [],
      companyIds: [],
      pageIndex: 0,
    };
    this.filterOptions = {
      locations: [],
      workingTimes: [],
      employmentTypes: [],
    };
    this.jobsTotal = 0;
    this.initialFilters = true;
    this.showFilter = {
      general: false,
      subFilters: {
        searchQuery: true,
        facilities: true,
        employmentTypes: true,
        workingTimes: true,
      },
    };
  }

  getJobs(pageIndex: number = null, companyId: number = null) {
    this.jobsLoading = true;
    this.jobs = [];
    this.currentFilter.pageIndex = pageIndex ?? this.currentFilter.pageIndex;
    const options = {
      search: this.currentFilter.searchQuery,
      facilities: this.currentFilter.facilities,
      workingTimes: this.currentFilter.workingTimes,
      employmentTypes: this.currentFilter.employmentTypes,
      companyIds: this.currentFilter.companyIds,
      offset:
        this.currentFilter.pageIndex >= 0
          ? this.LIMIT * this.currentFilter.pageIndex
          : 0,
      limit: this.currentFilter.pageIndex >= 0 ? this.LIMIT : -1,
    };
    this.appendCurrentFiltersToRoute();
    let request: Observable<{
      results: Job[];
      total: number;
      filterTotal: number;
    }>;
    if (companyId) {
      request = this.jobboardAdapter.getCompanyJobs(companyId, options);
    } else {
      request = this.jobboardAdapter.getJobs(options);
    }
    request
      .pipe(untilDestroyed(this))
      .pipe(take(1))
      .subscribe((result) => {
        this.jobs = result.results;
        this.jobsTotal = result.total;
        this.jobsLoading = false;
      });
    return request;
  }

  resetJobs() {
    this.jobs = [];
  }

  getFilterOptions(companyId: number = null) {
    const request = companyId
      ? this.jobboardAdapter.getCompanyFilterOptions(companyId)
      : this.jobboardAdapter.getFilterOptions();
    request.pipe(untilDestroyed(this)).subscribe(
      (filter: JobboardFilter) =>
        (this.filterOptions = {
          ...filter,
          workingTimes: this.constantsService.getLabelValueArray(
            this.constantsService.workingHoursConstants
          ),
          employmentTypes: this.constantsService.getLabelValueArray(
            this.constantsService.employmentTypeConstants
          ),
        })
    );
  }

  showPreselectedFilters() {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const queryParams = urlTree.queryParams;
    if (queryParams['hidePreselectedFilters'] === 'true') {
      if (queryParams['searchQuery'])
        this.showFilter.subFilters.searchQuery = false;
      if (queryParams['facilities'])
        this.showFilter.subFilters.facilities = false;
      if (queryParams['employmentTypes'])
        this.showFilter.subFilters.employmentTypes = false;
      if (queryParams['workingTimes'])
        this.showFilter.subFilters.workingTimes = false;
    }
  }

  setFilterByUrl() {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const queryParams = urlTree.queryParams;
    this.currentFilter.searchQuery = queryParams['searchQuery'] ?? '';
    this.currentFilter.facilities =
      queryParams['facilities'] !== ''
        ? queryParams['facilities']?.split(',') ?? []
        : [];
    this.currentFilter.employmentTypes =
      queryParams['employmentTypes'] !== ''
        ? queryParams['employmentTypes']?.split(',').map((entry) => +entry) ??
          []
        : [];
    this.currentFilter.workingTimes =
      queryParams['workingTimes'] !== ''
        ? queryParams['workingTimes']?.split(',').map((entry) => +entry) ?? []
        : [];
    this.currentFilter.companyIds =
      queryParams['companyIds'] !== ''
        ? queryParams['companyIds']
            ?.split(',')
            .map((entry) => +entry)
            .filter((id) => !Number.isNaN(id)) ?? []
        : [];
    this.currentFilter.pageIndex = queryParams['pageIndex']
      ? +queryParams['pageIndex']
      : 0;
  }

  appendCurrentFiltersToRoute(): void {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);

    const {
      searchQuery,
      facilities,
      employmentTypes,
      workingTimes,
      companyIds,
      ...rest
    } = urlTree.queryParams;
    urlTree.queryParams = rest;

    if (this.currentFilter.searchQuery !== '') {
      urlTree.queryParams['searchQuery'] = this.currentFilter.searchQuery;
    }
    if (this.currentFilter.facilities.length > 0) {
      urlTree.queryParams['facilities'] =
        this.currentFilter.facilities.join(',');
    }
    if (this.currentFilter.employmentTypes.length > 0) {
      urlTree.queryParams['employmentTypes'] =
        this.currentFilter.employmentTypes.join(',');
    }
    if (this.currentFilter.workingTimes.length > 0) {
      urlTree.queryParams['workingTimes'] =
        this.currentFilter.workingTimes.join(',');
    }
    if (this.currentFilter.companyIds.length > 0) {
      urlTree.queryParams['companyIds'] =
        this.currentFilter.companyIds.join(',');
    }

    urlTree.queryParams['pageIndex'] = this.currentFilter.pageIndex;

    this.router.navigateByUrl(urlTree);
  }

  get showFiltersInitial() {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const queryParams = urlTree.queryParams;
    if (queryParams['forceShowFilters'] === 'true') return true;
    return (
      this.jobsTotal > 25 ||
      this.currentFilter.employmentTypes.length ||
      this.currentFilter.facilities.length ||
      this.currentFilter.workingTimes.length ||
      this.currentFilter.searchQuery !== ''
    );
  }
}
